import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { useCreateAsset } from "@/hooks/useAssets";
import { useAllZonesFlat } from "@/hooks/useFacilityZones";
import type { AssetType, AssetCriticality } from "@/hooks/useAssets";
import { LocalImageDropZone } from "@/components/shared/LocalImageDropZone";
import { useUploadEntityImage } from "@/hooks/useEntityImages";
import { LocalFileDropZone } from "@/components/shared/LocalFileDropZone";
import { useUploadEntityFile } from "@/hooks/useEntityFiles";

const ASSET_TYPES: { value: AssetType; label: string }[] = [
  { value: "bottling", label: "Bottling" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "safety", label: "Safety" },
  { value: "wine_production", label: "Wine Production" },
  { value: "vehicle", label: "Vehicle" },
  { value: "tools", label: "Tools" },
  { value: "fluids", label: "Fluids" },
  { value: "tank", label: "Tank" },
];

export function CreateAssetDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [assetType, setAssetType] = useState<string>("");
  const [criticality, setCriticality] = useState<string>("normal");
  const [zoneId, setZoneId] = useState<string>("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const createAsset = useCreateAsset();
  const uploadImage = useUploadEntityImage("asset");
  const uploadFile = useUploadEntityFile("asset");
  const zones = useAllZonesFlat();

  const resetForm = () => {
    setName("");
    setDescription("");
    setAssetType("");
    setCriticality("normal");
    setZoneId("");
    setMake("");
    setModel("");
    setSerialNumber("");
    setPendingImages([]);
    setPendingFiles([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedZone = zones.find((z) => z.id === zoneId);
    createAsset.mutate(
      {
        name,
        description: description || undefined,
        asset_type: (assetType as AssetType) || undefined,
        criticality: (criticality as AssetCriticality) || "normal",
        facility_id: selectedZone?.facility_id || undefined,
        zone_id: zoneId || undefined,
        make: make || undefined,
        model: model || undefined,
        serial_number: serialNumber || undefined,
      },
      {
        onSuccess: (newAsset) => {
          pendingImages.forEach((file) =>
            uploadImage.mutate({ file, entityId: newAsset.id, orgId: newAsset.org_id })
          );
          pendingFiles.forEach((file) =>
            uploadFile.mutate({ file, entityId: newAsset.id, orgId: newAsset.org_id })
          );
          resetForm();
          setOpen(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Asset
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Asset</DialogTitle>
          <DialogDescription>Add equipment or machinery to the asset registry.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assetName">Name *</Label>
            <Input id="assetName" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., GAI Bottling Line" required />
          </div>

          <div className="space-y-2">
            <Label htmlFor="assetDesc">Description</Label>
            <Textarea id="assetDesc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={assetType} onValueChange={setAssetType}>
                <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Criticality</Label>
              <Select value={criticality} onValueChange={setCriticality}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Location</Label>
            <Select value={zoneId} onValueChange={setZoneId}>
              <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
              <SelectContent>
                {zones.map((z) => (
                  <SelectItem key={z.id} value={z.id}>{z.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Make</Label>
              <Input value={make} onChange={(e) => setMake(e.target.value)} placeholder="Brand" />
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="Model #" />
            </div>
            <div className="space-y-2">
              <Label>Serial #</Label>
              <Input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Photos</Label>
            <LocalImageDropZone files={pendingImages} onChange={setPendingImages} />
          </div>

          <div className="space-y-2">
            <Label>Files & Manuals</Label>
            <LocalFileDropZone files={pendingFiles} onChange={setPendingFiles} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={createAsset.isPending}>
              {createAsset.isPending ? "Creating..." : "Create Asset"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
